import * as React from 'react';

import { cn } from '../../utils/cn';

/** Visual style for a toast action button. */
export type ToastActionButtonVariant = 'primary' | 'outline';

/**
 * Caller-owned action for a toast notification.
 * Supply a stable `id`, an accessible `label`, and either an icon, text, or both.
 */
export type ToastNotificationAction = {
    /** Stable identity used as the React key. */
    id: string;
    /** Accessible name announced to assistive tech. */
    label: string;
    /** Visible label; omit for icon-only actions. */
    text?: React.ReactNode;
    /** Optional leading icon (caller-owned glyph). */
    icon?: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    /** Button chrome; defaults to solid primary. */
    variant?: ToastActionButtonVariant;
};

type ToastActionButtonProps = {
    action: ToastNotificationAction;
};

/**
 * Action control for ToastNotification. Supports icon-only, text, or both.
 * Icon-only actions rely on `action.label` as the accessible name; text actions
 * expose their visible label and keep `aria-label` only when it differs.
 * Sizes use the numeric scale (step = design_px / 4) so BM rem ≈ 1px still
 * resolves to design pixels.
 */
export function ToastActionButton({ action }: ToastActionButtonProps) {
    const hasText = action.text != null && action.text !== '';
    const hasIcon = action.icon != null;
    const isIconOnly = hasIcon && !hasText;
    const isOutline = (action.variant ?? 'primary') === 'outline';
    const visibleText =
        typeof action.text === 'string' ? action.text : undefined;
    const ariaLabel =
        isIconOnly || (visibleText != null && visibleText !== action.label)
            ? action.label
            : undefined;

    return (
        <button
            type="button"
            aria-label={ariaLabel}
            disabled={action.disabled}
            onClick={action.onClick}
            className={cn(
                'inline-flex shrink-0 items-center justify-center transition-colors',
                'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1a1a] focus-visible:outline-hidden',
                'disabled:pointer-events-none disabled:opacity-50',
                isOutline
                    ? 'border border-white/60 bg-transparent text-[#f1f1f1] hover:bg-white/10'
                    : 'bg-white text-[#161616]! hover:bg-gray-100',
                isIconOnly
                    ? 'size-9.75 rounded-full'
                    : 'min-h-10 gap-1.75 rounded-full px-2.5 py-2.5 text-3.75 leading-[1.2] font-medium',
            )}
        >
            {hasIcon ? (
                <span
                    className={cn(
                        'inline-flex shrink-0 items-center justify-center [&_svg]:size-4.75',
                        !isIconOnly && '[&_svg]:size-3.75',
                    )}
                    aria-hidden
                >
                    {action.icon}
                </span>
            ) : null}
            {hasText ? <span>{action.text}</span> : null}
        </button>
    );
}
